/** @prettier */

import {Grid, Paper, Container, Box, IconButton, Button} from '@mui/material';
import MailOutlineOutlinedIcon from '@mui/icons-material/MailOutlineOutlined';
import {useState, useEffect} from 'react';
import {useLoaderData, useParams} from 'react-router-dom';
import type {LoaderFunctionArgs} from 'react-router-dom';
import {useTranslation} from 'react-i18next';

//MTH component import
import LogicSettingDialog from './LogicSettingDialog';
import LogicDeleteDialog from './LogicDeleteDialog';
import ChangePriorityLevelsDialog from './LogicSortTableDialog';
import LogicActionControlContents from './LogicActionControlIconContents';
import {AddButton} from '../../component/BasicButton';
//API import
import {
  getSettingLogic,
  getCompositionsSitesBySiteID,
} from '../../API/API_Collection';
//interface import
import * as apiIF from '../../Type/api';
//Style import
import '../../css/common/common.css';

import {iconBlack, iconWhite} from '../../define';
import {checkUnitExistence} from '../../functions/checkUnitExistence';
import type {unitExistence} from '../../functions/checkUnitExistence';

export async function loader({
  params,
}: LoaderFunctionArgs): Promise<apiIF.res.getSettingLogic | undefined> {
  //パスパラメータを取得できない場合
  if (params.siteID === undefined) {
    return undefined;
  }

  try {
    const promises = [];
    //ロジック設定
    promises.push(getSettingLogic(params.siteID));
    //サイトの情報を取得
    promises.push(getCompositionsSitesBySiteID(params.siteID));
    //全てのユニットの登録状況を取得
    promises.push(checkUnitExistence(params.siteID, true));
    const result = await Promise.all(promises);

    const siteInfo = result[1] as apiIF.res.getCompositionsSitesBySiteID;
    const unitExistence = result[2] as unitExistence;

    if (
      unitExistence.hasAirconIU === false ||
      siteInfo['site-authority'] !== 'admin'
    ) {
      throw new Error(
        'Required equipment not registered or insufficient permission'
      );
    }

    return result[0] as apiIF.res.getSettingLogic;
  } catch {
    return undefined;
  }
}

/**
 * ロジック
 * @returns JSX
 */
const Logic = () => {
  const trans = useTranslation().t; //翻訳関数
  const loaderData = useLoaderData() as apiIF.res.getSettingLogic | undefined;
  //パスパラメータ取得
  const {siteID} = useParams();

  //prettier-ignore
  //サイトのロジック設定情報
  const [logicInfo, setLogicInfo] = useState<apiIF.res.getSettingLogic | null>(null);
  //prettier-ignore
  //ロジック優先レベル一覧
  const [priorityLevels, setPriorityLevels] = useState<apiIF.res.settingLogic['priority-level'][]>([]);
  //prettier-ignore
  //未設定のロジック設定一覧
  const [unsetLogicSettings, setUnsetLogicSettings] = useState<apiIF.res.getSettingLogic['settings']>([]);
  //prettier-ignore
  //未設定のロジック優先レベル一覧
  const [unsetPriorityLevels, setUnsetPriorityLevels] = useState<apiIF.res.settingLogic['priority-level'][]>([]);
  //選択状態のロジック
  const [selectedLogic, setSelectedLogic] = useState('');
  //選択状態の行
  const [selectedRow, setSelectedRow] = useState('');
  //サイトのロジック設定の更新フラグ
  const [hasLogicInfoChanged, setHasLogicInfoChanged] = useState(false);
  //API応答待ちフラグ
  const [apiWait, setAPIWait] = useState(false);
  //ダイアログ表示フラグ
  const [isOpenAddDialog, setIsOpenAddDialog] = useState(false); //追加ダイアログ
  const [isOpenSortTableDialog, setIsOpenSortTableDialog] = useState(false); //優先度入れ替え表ダイアログ
  const [isOpenEditDialog, setIsOpenEditDialog] = useState(false); //編集ダイアログ
  const [isOpenDeleteDialog, setIsOpenDeleteDialog] = useState(false); //削除ダイアログ

  //ロジック表の幅一覧（合計を12にすること）
  const gridXsNumber = 0.7;
  const gridXsTarget = 1.8;
  const gridXsCondition = 2;
  const gridXsParameter = 2;
  const gridXsAction = 3.5;
  const gridXsOperation = 2;

  useEffect(() => {
    if (loaderData === undefined) {
      return;
    }
    setLogicInfo(loaderData);
    const {setLevel, unSetLevel} = dividePriorityLevels(loaderData.settings);
    setPriorityLevels(setLevel);
    setUnsetLogicSettings(getUnsetLogicSettings(loaderData.settings));
    setUnsetPriorityLevels(unSetLevel);
  }, [loaderData]);

  //ロジック設定の追加、編集、削除時に情報を再取得する
  useEffect(() => {
    //パスパラメータを取得できない場合
    if (siteID === undefined) {
      return;
    }
    //ロジック設定更新時のみAPI実行
    if (hasLogicInfoChanged === false) {
      return;
    }

    setAPIWait(true);
    //サイトのロジック設定情報取得
    getSettingLogic(siteID)
      .then(response => {
        setLogicInfo(response);
        const {setLevel, unSetLevel} = dividePriorityLevels(response.settings);
        setPriorityLevels(setLevel);
        setUnsetLogicSettings(getUnsetLogicSettings(response.settings));
        setUnsetPriorityLevels(unSetLevel);
        setHasLogicInfoChanged(false);
      })
      .finally(() => {
        setAPIWait(false);
      });
  }, [hasLogicInfoChanged, siteID]);

  /**
   * ロジック優先レベルを設定済みと未設定に分ける
   * @param logicSettings ロジック設定情報
   * @returns ロジック優先レベルの配列
   */
  const dividePriorityLevels = (
    logicSettings: apiIF.res.getSettingLogic['settings']
  ) => {
    /** 設定済みの優先レベル */
    const setLevel: apiIF.res.settingLogic['priority-level'][] = [];
    /** 未設定の優先レベル */
    const unSetLevel: apiIF.res.settingLogic['priority-level'][] = [];

    logicSettings.forEach(logic => {
      if (logic.control === '') {
        //未設定
        unSetLevel.push(logic['priority-level']);
      } else {
        //設定済み
        setLevel.push(logic['priority-level']);
      }
    });

    return {setLevel, unSetLevel};
  };

  /**
   * 未設定のロジック情報を取得
   * @param logicSettings ロジック設定情報
   * @returns 未設定のロジック優先レベルを含む配列
   */
  const getUnsetLogicSettings = (
    logicSettings: apiIF.res.getSettingLogic['settings']
  ) => {
    //有効無効設定が空である場合、未設定と判定する
    const result = logicSettings.filter(logic => {
      return logic['control'] === '';
    });
    return result;
  };

  /**
   * ADD LOGICボタン押下時処理
   */
  const handleAddLogic = () => {
    setIsOpenAddDialog(true);
  };
  /**
   * Sort LOGICボタン押下時処理
   */
  const handleSortLogic = () => {
    setIsOpenSortTableDialog(true);
  };

  /**
   * Editボタン押下時処理
   * @param priorityLevel ロジックの優先レベル
   */
  const openEditDialog = (
    priorityLevel: apiIF.res.settingLogic['priority-level']
  ) => {
    setIsOpenEditDialog(true);
    setSelectedLogic(priorityLevel);
    setSelectedRow(priorityLevel);
  };
  /**
   * Deleteボタン押下時処理
   * @param priorityLevel ロジックの優先レベル
   */
  const openDeleteDialog = (
    priorityLevel: apiIF.res.settingLogic['priority-level']
  ) => {
    setIsOpenDeleteDialog(true);
    setSelectedLogic(priorityLevel);
    setSelectedRow(priorityLevel);
  };

  /**
   * ロジック行が選択状態か判定
   * @param row 行
   */
  const selectHandle = (row: string) => {
    if (
      row === selectedRow &&
      isOpenEditDialog === false &&
      isOpenDeleteDialog === false
    ) {
      setSelectedRow('');
    } else {
      setSelectedRow(row);
    }
  };

  /**
   * 温度範囲パラメータ(more or less)をチェックして表示する文言を決定する
   * @param {'or-more' | 'or-less'} value 温度範囲パラメータ range : 'or-more' | 'or-less'
   * @returns trans(翻訳文)
   */
  const parameterCheck = (value: '' | 'or-more' | 'or-less') => {
    switch (value) {
      case 'or-more':
        return trans('Logic.parameter.orMore');
      case 'or-less':
        return trans('Logic.parameter.orLess');
      default:
        return '';
    }
  };

  /**
   * ロジックを表示するテーブルの一列を作成
   * @param props props
   * @returns JSX
   */
  const LogicBody = (props: {
    /** 表示するロジック */
    logic: apiIF.res.settingLogic;
    /** 表示するロジックの位置 */
    index: number;
    /** サイトのロジック設定情報 */
    logicInfo: apiIF.res.getSettingLogic;
  }) => {
    /** テーブル一列のクラス */
    const bodyClass = `logic__list__body__row ${
      props.logic['priority-level'] === selectedRow
        ? 'logic__list__body__row--selected'
        : ''
    }`;
    /** 翻訳後の対象の名前 */
    let targetName = '';
    switch (props.logic.scope) {
      case 'site':
        targetName = trans('Logic.list.contents.target.allUnit');
        break;
      case 'floor':
        if (Number(props.logic['target-name']) >= 0) {
          //prettier-ignore
          targetName = trans(`Logic.list.contents.target.floor${props.logic['target-name']}`);
        } else if (Number(props.logic['target-name']) >= -4) {
          //prettier-ignore
          targetName = trans(`Logic.list.contents.target.basement${props.logic['target-name'].replace('-', '')}`);
        }
        break;
      case 'group':
      case 'sl': //fall through
        targetName = props.logic['target-name'];
        break;
    }

    return (
      <Grid
        container
        alignItems="center"
        className={bodyClass}
        onClick={() => selectHandle(props.logic['priority-level'])}
        key={props.logic['priority-level']}>
        {/** 番号 */}
        <Grid
          item
          xs={gridXsNumber}
          className="logic__list__body__row__data__no">
          {props.index + 1}
        </Grid>
        {/** 対象 */}
        <Grid
          item
          xs={gridXsTarget}
          title={targetName}
          className="logic__list__body__row__data__target">
          {targetName}
        </Grid>
        {/** 状態値 */}
        <Grid
          item
          xs={gridXsCondition}
          className="logic__list__body__row__data__condition">
          {props.logic['trigger'] === 'outdoor-temperature'
            ? `${trans('Logic.list.contents.condition.outdoorTemp')}`
            : `${trans('Logic.list.contents.condition.roomTemp')}`}
        </Grid>
        {/** 条件 */}
        <Grid
          item
          xs={gridXsParameter}
          className="logic__list__body__row__data__parameter">
          {`${props.logic['threshold']} ${parameterCheck(
            props.logic['range']
          )}`}
        </Grid>
        {/** 動作 */}
        <Grid
          item
          xs={gridXsAction}
          className="logic__list__body__row__data__action">
          {/* 制御 or 通知を表示*/}
          {'notify-authority' in props.logic['action'] ? (
            <Grid container alignItems="center">
              <Grid item xs={3}>
                <Box className="logic__list__body__row__data__action__mailIcon">
                  <MailOutlineOutlinedIcon className="logic__list__body__row__data__action__mailIcon__image" />
                </Box>
              </Grid>
              <Grid
                item
                xs={6}
                className="logic__list__body__row__data__action__authority">
                {props.logic['action']['notify-authority']}
              </Grid>
            </Grid>
          ) : (
            <LogicActionControlContents logicInfo={props.logic} />
          )}
        </Grid>
        {/* ロジック設定メニュー */}
        <Grid
          item
          xs={gridXsOperation}
          className="logic__list__body__row__data__operationIcon">
          <IconButton
            size="small"
            onClick={e => {
              e.stopPropagation();
              openEditDialog(props.logic['priority-level']);
            }}>
            <Box className="logic__list__body__row__data__operationIcon__icon__border">
              <img
                title={`${trans(`Logic.image.title.${iconWhite.EDIT.title}`)}`}
                src={iconBlack.EDIT.src}
                alt={`${trans(`Logic.image.title.${iconWhite.EDIT.alt}`)}`}
                className="logic__list__body__row__data__operationIcon__icon__border__image"
              />
            </Box>
          </IconButton>
          <IconButton
            size="small"
            onClick={e => {
              e.stopPropagation();
              openDeleteDialog(props.logic['priority-level']);
            }}>
            <Box className="logic__list__body__row__data__operationIcon__icon__border">
              <img
                title={`${trans(
                  `Logic.image.title.${iconWhite.DELETE.title}`
                )}`}
                src={iconBlack.DELETE.src}
                alt={`${trans(`Logic.image.title.${iconWhite.DELETE.alt}`)}`}
                className="logic__list__body__row__data__operationIcon__icon__border__image"
              />
            </Box>
          </IconButton>
          {props.logic['priority-level'] === selectedLogic ? (
            <>
              <LogicSettingDialog
                logicInfo={props.logicInfo}
                targetLogic={props.logic}
                isOpenDialog={isOpenEditDialog}
                setIsOpenDialog={setIsOpenEditDialog}
                setLogicInfoChange={setHasLogicInfoChanged}
              />
              <LogicDeleteDialog
                logicInfo={props.logicInfo}
                targetPriorityLevel={props.logic['priority-level']}
                targetLogicName={targetName}
                targetLogicNumber={props.index + 1}
                isOpenDialog={isOpenDeleteDialog}
                setIsOpenDialog={setIsOpenDeleteDialog}
                setLogicInfoChange={setHasLogicInfoChanged}
              />
            </>
          ) : (
            <></>
          )}
        </Grid>
      </Grid>
    );
  };

  //サイトのロジック設定情報を取得できない場合
  if (logicInfo === null) {
    return <></>;
  }
  return (
    <Container fixed>
      <Grid container rowSpacing={3}>
        {/** タイトル、説明 */}
        <Grid item xs={12}>
          <h1>{trans('Logic.title')}</h1>
          <h2>{trans('Logic.explanation')}</h2>
          <h2>{trans('Logic.example')}</h2>
          <Box className="logic__header__note">
            {trans('Logic.note')}
            <ul>
              <li>{trans('Logic.message1')}</li>
              <li>{trans('Logic.message2')}</li>
            </ul>
          </Box>
        </Grid>
        {/** 並び替えボタン、追加ボタン */}
        <Grid
          item
          xs={12}
          container
          justifyContent="flex-end"
          alignItems="center"
          spacing={3}>
          {/* 並び替えボタン */}
          <Grid item xs="auto">
            {/* 未設定のロジックがない場合は無効 */}
            <Button
              onClick={handleSortLogic}
              color="primary"
              variant="outlined"
              id="mv-btnSortLogic"
              disabled={apiWait || priorityLevels.length === 0}>
              {trans('Logic.buttonText.sort')}
            </Button>
          </Grid>
          {/* 追加ボタン */}
          <Grid item xs="auto">
            {/* 未設定のロジックがない場合は無効 */}
            <AddButton
              disabled={apiWait || unsetPriorityLevels.length === 0}
              onClick={handleAddLogic}
              id="mv-btnAddLogic"
              buttonText={trans('Logic.buttonText.add')}
            />
          </Grid>
        </Grid>
        {/** ロジック一覧 */}
        <Grid item xs={12}>
          <Grid container component={Paper}>
            {/** ヘッダ */}
            <Grid item xs={gridXsNumber} className="logic__list__header">
              {trans('Logic.list.header.number')}
            </Grid>
            <Grid item xs={gridXsTarget} className="logic__list__header">
              {trans('Logic.list.header.target')}
            </Grid>
            <Grid item xs={gridXsCondition} className="logic__list__header">
              {trans('Logic.list.header.condition')}
            </Grid>
            <Grid item xs={gridXsParameter} className="logic__list__header">
              {trans('Logic.list.header.parameter')}
            </Grid>
            <Grid
              item
              xs={gridXsAction + gridXsOperation}
              className="logic__list__header">
              {trans('Logic.list.header.action')}
            </Grid>
            {/* 設定済みのロジックを表示する */}
            <Grid item xs={12} className="logic__list__body">
              {logicInfo['settings']
                .filter(logic => {
                  return (
                    logic['control'] === 'active' ||
                    logic['control'] === 'inactive'
                  );
                })
                .map((logic, index) => (
                  <LogicBody
                    logic={logic}
                    index={index}
                    logicInfo={logicInfo}
                    key={index}
                  />
                ))}
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      {/** 優先度入れ替え表ダイアログ */}
      <ChangePriorityLevelsDialog
        siteID={logicInfo['site-id']}
        priorityLevels={priorityLevels}
        isOpenDialog={isOpenSortTableDialog}
        setIsOpenDialog={setIsOpenSortTableDialog}
        setLogicInfoChange={setHasLogicInfoChanged}
      />
      {/** 追加ダイアログ */}
      <LogicSettingDialog
        logicInfo={logicInfo}
        targetLogic={
          unsetLogicSettings.length === 0 ? null : unsetLogicSettings[0]
        }
        isOpenDialog={isOpenAddDialog}
        setIsOpenDialog={setIsOpenAddDialog}
        setLogicInfoChange={setHasLogicInfoChanged}
      />
    </Container>
  );
};

export default Logic;
